import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Company } from '../types/index';
import { authService } from '../services/auth.service';
import { companiesService } from '../services/companies.service';
import {
  tokenStore,
  coreLogin,
  coreLoginPin,
  coreMemberships,
  coreLogout,
} from '../services/core';

interface AuthContextData {
  user: User | null;
  token: string | null;
  companyId: string | null;
  companies: Company[];
  loading: boolean;
  login: (email: string, senha: string) => Promise<void>;
  loginWithPin: (email: string, pin: string) => Promise<void>;
  setActiveCompany: (companyId: string) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);

  // Lista de empresas do usuário (pro CompanySwitcher). Best-effort.
  const loadCompanies = async () => {
    try {
      setCompanies(await companiesService.getAll());
    } catch {
      /* ignora — switcher só não aparece */
    }
  };

  // Garante uma empresa ativa: usa a armazenada ou escolhe a 1ª membership do Core.
  const ensureActiveCompany = async (access: string): Promise<string | null> => {
    let cid = tokenStore.getCompanyId();
    if (!cid) {
      const memberships = await coreMemberships(access).catch(() => []);
      cid = memberships[0]?.company_id ?? null;
      if (cid) tokenStore.setCompanyId(cid);
    }
    return cid;
  };

  // Resolve o usuário (identidade do Core + papel na empresa ativa) via /auth/me do Task.
  const resolveSession = async (access: string) => {
    const cid = await ensureActiveCompany(access);
    setCompanyId(cid);
    const me = await authService.getProfile();
    setToken(access);
    setUser(me);
    localStorage.setItem('user', JSON.stringify(me));
    loadCompanies();
  };

  useEffect(() => {
    const access = tokenStore.getAccess();
    if (!access) {
      setLoading(false);
      return;
    }
    // Hidratação otimista pelo cache + revalidação via Core.
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      setToken(access);
      setUser(JSON.parse(cachedUser));
      setCompanyId(tokenStore.getCompanyId());
    }
    resolveSession(access)
      .catch(() => {
        // Token inválido/expirado e refresh indisponível → desloga.
        tokenStore.clear();
        setToken(null);
        setUser(null);
        setCompanyId(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, senha: string) => {
    const { access_token, refresh_token } = await coreLogin(email, senha);
    tokenStore.setTokens(access_token, refresh_token);
    await resolveSession(access_token);
  };

  const loginWithPin = async (email: string, pin: string) => {
    const { access_token, refresh_token } = await coreLoginPin(email, pin);
    tokenStore.setTokens(access_token, refresh_token);
    await resolveSession(access_token);
  };

  const setActiveCompany = async (cid: string) => {
    tokenStore.setCompanyId(cid);
    setCompanyId(cid);
    const access = tokenStore.getAccess();
    if (access) {
      const me = await authService.getProfile();
      setUser(me);
      localStorage.setItem('user', JSON.stringify(me));
    }
  };

  const logout = () => {
    const refresh = tokenStore.getRefresh();
    setUser(null);
    setToken(null);
    setCompanyId(null);
    setCompanies([]);
    tokenStore.clear();
    coreLogout(refresh).catch(() => {});
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  return (
    <AuthContext.Provider
      value={{ user, token, companyId, companies, loading, login, loginWithPin, setActiveCompany, logout, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
